import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AuthShell, { GoogleButton, inputCls, primaryBtn } from "@/components/AuthShell";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { homeFor } from "@/pages/Login";
import { Megaphone, User, Scale, Plane, HeartPulse, Stethoscope } from "lucide-react";
import { EnterpriseCrmSection } from "@/components/EnterpriseCrm";
import CrmReadinessBadge from "@/components/CrmReadinessBadge";

const ROLES = [
  { key: "client", label: "Client", icon: User },
  { key: "attorney", label: "Attorney", icon: Scale },
  { key: "affiliate", label: "Affiliate", icon: Megaphone },
  { key: "pilot", label: "Pilot", icon: Plane },
  { key: "nurse", label: "Nurse", icon: Stethoscope },
  { key: "telehealth", label: "Provider", icon: HeartPulse },
];

const COPY = {
  client: { title: "Create your account", subtitle: "Be ready before you ever need to be." },
  attorney: { title: "Attorney / Firm Application", subtitle: "Apply for access to the referral board and Firm Command Center." },
  affiliate: { title: "Become an Affiliate", subtitle: "Promote Crash Ready and earn commission on referred cases." },
  pilot: { title: "Join the Response Network", subtitle: "Apply to receive scene-documentation dispatches near you." },
  nurse: { title: "Join the RN Triage Network", subtitle: "Apply to perform complimentary Crash Ready nurse intakes." },
  telehealth: { title: "Telehealth Provider Application", subtitle: "Apply to join the Crash Ready medical coordination network." },
};

const Field = ({ label, hint, children }) => (
  <div>
    <label className="text-sm font-medium text-slate-700">{label}{hint && <span className="text-slate-400 font-normal"> {hint}</span>}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRole = ROLES.some((r) => r.key === params.get("role")) ? params.get("role")
    : params.get("affiliate") === "1" ? "affiliate" : "client";
  const [role, setRole] = useState(initialRole);
  const [f, setF] = useState({
    name: "", email: "", phone: "", password: "", referral_code: "",
    firm_name: "", bar_state: "", bar_number: "", office_location: "",
    state: "", city: "", service_radius_mi: 50, provider_type: "drone_pilot",
    faa_cert: "", insurance_status: "have_liability", equipment: "",
    n_first: "", n_last: "", n_license: "", n_states: "", n_npi: "", n_years: "",
    n_school: "", n_degrees: "", n_certs: "", n_address: "",
    n_trauma: false, n_ed: false, n_cm: false,
    t_legal: "", t_practice: "", t_orgtype: "telehealth_medical_practice",
    t_ptype: "telehealth_medical_practice", t_states: "", t_timezone: "", t_title: "",
  });
  const [loading, setLoading] = useState(false);
  const justRegistered = useRef(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { if (user && !justRegistered.current) navigate(homeFor(user)); }, [user, navigate]);
  useEffect(() => { const r = params.get("ref"); if (r) setF((p) => ({ ...p, referral_code: r.toUpperCase() })); }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    justRegistered.current = true;
    try {
      const opts = {
        phone: f.phone || undefined,
        as_affiliate: role === "affiliate",
        as_attorney: role === "attorney",
        as_pilot: role === "pilot",
        as_nurse: role === "nurse",
        as_telehealth: role === "telehealth",
      };
      if (role === "client") opts.referral_code = f.referral_code || undefined;
      if (role === "attorney") Object.assign(opts, { firm_name: f.firm_name, bar_state: f.bar_state, bar_number: f.bar_number, office_location: f.office_location });
      if (role === "pilot") Object.assign(opts, {
        state: f.state, city: f.city, service_radius_mi: Number(f.service_radius_mi),
        provider_type: f.provider_type, faa_cert: f.faa_cert, insurance_status: f.insurance_status, equipment: f.equipment,
      });
      if (role === "nurse") opts.nurse_profile = {
        first_name: f.n_first, last_name: f.n_last, phone: f.phone, address: f.n_address,
        license_number: f.n_license, states_licensed: f.n_states.split(",").map((s) => s.trim()).filter(Boolean),
        npi: f.n_npi, years_experience: f.n_years, nursing_school: f.n_school, degrees: f.n_degrees,
        certifications: f.n_certs.split(",").map((s) => s.trim()).filter(Boolean),
        trauma_experience: f.n_trauma, ed_experience: f.n_ed, case_management_experience: f.n_cm,
      };
      if (role === "telehealth") opts.telehealth_profile = {
        legal_name: f.t_legal, practice_name: f.t_practice, org_type: f.t_orgtype,
        provider_type: f.t_ptype, main_phone: f.phone, timezone: f.t_timezone,
        contact_name: f.name, contact_title: f.t_title, contact_mobile: f.phone,
        states: f.t_states.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
      };
      const u = await register(f.name, f.email, f.password, opts);
      if (role === "attorney") {
        const approved = (u.roles || []).includes("attorney");
        toast.success(approved ? "Attorney account approved!" : "Application submitted — pending admin approval.");
        navigate(approved ? "/attorney" : "/attorney-pending");
      } else if (role === "pilot") {
        toast.success("Pilot application submitted — pending admin approval.");
        navigate("/pilot-portal");
      } else if (role === "nurse") {
        toast.success("RN application submitted — pending admin approval.");
        navigate("/nurse-portal");
      } else if (role === "telehealth") {
        toast.success("Provider account created — complete your application in the portal.");
        navigate("/telehealth-portal");
      } else if (role === "affiliate") {
        toast.success("Affiliate account created — pending admin approval.");
        navigate("/affiliate");
      } else {
        toast.success("Account created!");
        navigate(homeFor(u) + "?welcome=1");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  const idx = ROLES.findIndex((r) => r.key === role);

  return (
    <AuthShell title={COPY[role].title} subtitle={COPY[role].subtitle}
      footer={<>Already have an account? <Link to="/login" className="text-cobalt font-medium">Log in</Link></>}>

      <div className="relative grid grid-cols-6 bg-slate-100 rounded-full p-1 mb-6" data-testid="signup-type">
        <div className="absolute top-1 bottom-1 rounded-full bg-white shadow-sm transition-transform duration-300 ease-out"
          style={{ width: "calc((100% - 0.5rem)/6)", transform: `translateX(${idx * 100}%)` }} />
        {ROLES.map((r) => {
          const Icon = r.icon; const on = role === r.key;
          return (
            <button key={r.key} type="button" onClick={() => setRole(r.key)} data-testid={`signup-${r.key}`}
              className={`relative z-10 flex items-center justify-center gap-1 py-2.5 rounded-full text-xs sm:text-sm font-medium transition-colors ${on ? "text-navy" : "text-slate-500"}`}>
              <Icon className="w-4 h-4" /> <span className="hidden sm:inline">{r.label}</span>
            </button>
          );
        })}
      </div>

      {role === "attorney" && <div className="mb-5 space-y-3"><CrmReadinessBadge /><EnterpriseCrmSection compact /></div>}

      <form onSubmit={submit} className="space-y-4">
        <Field label="Full name">
          <input data-testid="register-name" required value={f.name} onChange={set("name")} className={inputCls} placeholder={role === "attorney" ? "Jane Attorney" : "Jane Driver"} />
        </Field>
        <Field label="Email">
          <input data-testid="register-email" type="email" required value={f.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" />
        </Field>
        {role !== "client" && (
          <Field label="Phone">
            <input data-testid="register-phone" value={f.phone} onChange={set("phone")} className={inputCls} placeholder="+1 415 555 1234" />
          </Field>
        )}
        <Field label="Password">
          <input data-testid="register-password" type="password" required minLength={6} value={f.password} onChange={set("password")} className={inputCls} placeholder="At least 6 characters" />
        </Field>

        {role === "attorney" && (
          <>
            <Field label="Firm name"><input data-testid="register-firm" required value={f.firm_name} onChange={set("firm_name")} className={inputCls} placeholder="Keith & Associates" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Bar state"><input data-testid="register-bar-state" value={f.bar_state} onChange={set("bar_state")} className={inputCls} placeholder="TX" /></Field>
              <Field label="Bar number"><input data-testid="register-bar-number" value={f.bar_number} onChange={set("bar_number")} className={inputCls} placeholder="24099999" /></Field>
            </div>
            <Field label="Office location"><input data-testid="register-office" value={f.office_location} onChange={set("office_location")} className={inputCls} placeholder="Dallas, TX" /></Field>
          </>
        )}

        {role === "pilot" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="State"><input data-testid="register-state" required value={f.state} onChange={set("state")} className={inputCls} placeholder="TX" /></Field>
              <Field label="City"><input data-testid="register-city" value={f.city} onChange={set("city")} className={inputCls} placeholder="Dallas" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Response role">
                <select data-testid="register-provider-type" value={f.provider_type} onChange={set("provider_type")} className={inputCls}>
                  <option value="drone_pilot">Drone Pilot (FAA Part 107)</option>
                  <option value="investigator">Field Investigator</option>
                  <option value="photographer">Scene Photographer</option>
                  <option value="other">Other</option>
                </select>
              </Field>
              <Field label="Availability radius (mi)"><input data-testid="register-radius" type="number" value={f.service_radius_mi} onChange={set("service_radius_mi")} className={inputCls} /></Field>
            </div>
            <Field label="FAA Part 107 certificate #" hint="(upload the certificate later in your profile)">
              <input data-testid="register-faa" value={f.faa_cert} onChange={set("faa_cert")} className={inputCls} placeholder="4123456" />
            </Field>
            <Field label="Insurance status">
              <select data-testid="register-insurance" value={f.insurance_status} onChange={set("insurance_status")} className={inputCls}>
                <option value="have_liability">Have liability insurance</option>
                <option value="have_hull">Have hull + liability</option>
                <option value="none_yet">Not yet — will provide</option>
                <option value="expired">Expired / renewing</option>
              </select>
            </Field>
            <Field label="Equipment"><input data-testid="register-equipment" value={f.equipment} onChange={set("equipment")} className={inputCls} placeholder="DJI Mavic 3, 4K camera" /></Field>
            <p className="text-xs text-slate-400">You'll upload your FAA certificate and insurance documents from your Pilot Profile after signing in.</p>
          </>
        )}

        {role === "nurse" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name"><input data-testid="register-n-first" required value={f.n_first} onChange={set("n_first")} className={inputCls} placeholder="Jane" /></Field>
              <Field label="Last name"><input data-testid="register-n-last" required value={f.n_last} onChange={set("n_last")} className={inputCls} placeholder="Nightingale" /></Field>
            </div>
            <Field label="Home address"><input data-testid="register-n-address" value={f.n_address} onChange={set("n_address")} className={inputCls} placeholder="Street, City, State ZIP" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nursing license #"><input data-testid="register-n-license" required value={f.n_license} onChange={set("n_license")} className={inputCls} placeholder="RN123456" /></Field>
              <Field label="State(s) licensed" hint="(comma-sep)"><input data-testid="register-n-states" value={f.n_states} onChange={set("n_states")} className={inputCls} placeholder="TX, OK" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="NPI" hint="(if applicable)"><input data-testid="register-n-npi" value={f.n_npi} onChange={set("n_npi")} className={inputCls} placeholder="1234567890" /></Field>
              <Field label="Years of experience"><input data-testid="register-n-years" value={f.n_years} onChange={set("n_years")} className={inputCls} placeholder="8" /></Field>
            </div>
            <Field label="Nursing school"><input data-testid="register-n-school" value={f.n_school} onChange={set("n_school")} className={inputCls} placeholder="UT Austin School of Nursing" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Degree(s)"><input data-testid="register-n-degrees" value={f.n_degrees} onChange={set("n_degrees")} className={inputCls} placeholder="BSN, MSN" /></Field>
              <Field label="Certifications" hint="(comma-sep)"><input data-testid="register-n-certs" value={f.n_certs} onChange={set("n_certs")} className={inputCls} placeholder="BLS, ACLS, TNCC" /></Field>
            </div>
            <div className="flex flex-wrap gap-4 pt-1">
              {[["n_trauma", "Trauma experience"], ["n_ed", "ED experience"], ["n_cm", "Case management"]].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={`register-${k}`} className="h-4 w-4 accent-cobalt" /> {label}
                </label>
              ))}
            </div>
            <p className="text-xs text-slate-400">You'll upload your resume, license verification, government ID, profile photo, and banking details from your RN Portal after signing in. Your account stays inactive until an admin approves it.</p>
          </>
        )}

        {role === "telehealth" && (
          <>
            <Field label="Legal organization name"><input data-testid="register-t-legal" required value={f.t_legal} onChange={set("t_legal")} className={inputCls} placeholder="Lone Star Telehealth PLLC" /></Field>
            <Field label="Public practice name"><input data-testid="register-t-practice" required value={f.t_practice} onChange={set("t_practice")} className={inputCls} placeholder="Lone Star Concussion Care" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Organization type">
                <select data-testid="register-t-orgtype" value={f.t_orgtype} onChange={set("t_orgtype")} className={inputCls}>
                  <option value="telehealth_medical_practice">Telehealth medical practice</option>
                  <option value="physician_group">Physician group</option>
                  <option value="neurology_practice">Neurology practice</option>
                  <option value="concussion_clinic">Concussion clinic</option>
                  <option value="other">Other</option>
                </select>
              </Field>
              <Field label="Provider type">
                <select data-testid="register-t-ptype" value={f.t_ptype} onChange={set("t_ptype")} className={inputCls}>
                  <option value="telehealth_medical_practice">Telehealth medical practice</option>
                  <option value="physician">Physician</option>
                  <option value="nurse_practitioner">Nurse practitioner</option>
                  <option value="physician_assistant">Physician assistant</option>
                  <option value="neurology_practice">Neurology practice</option>
                  <option value="concussion_clinic">Concussion clinic</option>
                  <option value="other">Other</option>
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Service states" hint="(comma-sep)"><input data-testid="register-t-states" required value={f.t_states} onChange={set("t_states")} className={inputCls} placeholder="TX, FL" /></Field>
              <Field label="Your role / title"><input data-testid="register-t-title" value={f.t_title} onChange={set("t_title")} className={inputCls} placeholder="Practice Administrator" /></Field>
            </div>
            <p className="text-xs text-slate-400">After creating your account you'll complete the full application — office locations, provider roster, licenses, insurance and secure document uploads — in your Provider Portal. Your organization cannot access client information until a Crash Ready administrator approves the application.</p>
          </>
        )}

        {role === "client" && (
          <Field label="Referral / Firm Code" hint="(optional)">
            <input data-testid="register-referral" value={f.referral_code} onChange={(e) => setF({ ...f, referral_code: e.target.value.toUpperCase() })} className={`${inputCls} uppercase`} placeholder="e.g. KEITHLAW or KYLEI10" />
          </Field>
        )}

        <button type="submit" disabled={loading} data-testid="register-submit" className={primaryBtn}>
          {loading ? "Submitting…" : role === "client" ? "Create account" : role === "attorney" ? "Submit attorney application" : role === "pilot" ? "Submit pilot application" : role === "nurse" ? "Submit RN application" : role === "telehealth" ? "Create provider account" : "Create affiliate account"}
        </button>
      </form>

      {role === "client" && (
        <>
          <div className="my-5 flex items-center gap-3 text-xs text-slate-400"><div className="flex-1 h-px bg-slate-200" />OR<div className="flex-1 h-px bg-slate-200" /></div>
          <GoogleButton label="Sign up with Google" />
        </>
      )}
    </AuthShell>
  );
}
